import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Avatar, Button, Chip, Box,
  List, ListItem, ListItemText, Divider, CircularProgress,
  AppBar, Toolbar, IconButton, Menu, MenuItem, Stack,
  Card, CardContent, Grid, alpha
} from '@mui/material';
import { 
  Assessment, PlayCircle, History, AccountCircle, 
  ExitToApp, Person, TrendingUp, School,
  EmojiEvents, Schedule, Grade
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import axios from '../../services/axios';
import { getCEFRColor, clearExamData } from '../../utils/helpers';

// Set locale to English
dayjs.locale('en');

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [stats, setStats] = useState({
    totalExams: 0,
    bestScore: 0,
    averageScore: 0,
    lastScore: 0,
    progress: 0
  });

  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);

  // Function to normalize TOEFL score
  const normalizeTOEFLScore = (score) => {
    if (!score && score !== 0) return 0;
    
    // Convert to number
    let numericScore = Number(score);
    
    // If score is NaN, return 0
    if (isNaN(numericScore)) return 0;
    
    console.log('🔧 Normalizing score:', { raw: score, normalized: numericScore });
    
    return numericScore;
  };

  // Function to calculate statistics correctly
  const calculateStats = (historyData) => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return {
        totalExams: 0,
        bestScore: 0,
        averageScore: 0,
        lastScore: 0,
        progress: 0
      };
    }

    // Extract and normalize scores
    const scores = historyData
      .map(h => {
        // Find score from various possible fields
        const score = h.total_score || h.total || h.score || h.toefl_score || 0;
        return normalizeTOEFLScore(score);
      })
      .filter(score => !isNaN(score) && score >= 0); // Filter valid scores

    console.log('📊 Processed scores:', scores);

    if (scores.length === 0) {
      return {
        totalExams: historyData.length,
        bestScore: 0,
        averageScore: 0,
        lastScore: 0,
        progress: 0
      };
    }

    const totalExams = scores.length;
    const bestScore = Math.max(...scores);
    const averageScore = Math.round(scores.reduce((a, b) => a + b, 0) / totalExams);
    const lastScore = scores[scores.length - 1];
    
    // Calculate progress (comparison of last score with previous average)
    let progress = 0;
    if (scores.length > 1) {
      const previousScores = scores.slice(0, -1);
      const previousAverage = previousScores.reduce((a, b) => a + b, 0) / previousScores.length;
      progress = previousAverage > 0 ? Math.round(((lastScore - previousAverage) / previousAverage) * 100) : 100;
    }

    console.log('📈 Final Statistics:', {
      scores,
      totalExams,
      bestScore,
      averageScore,
      lastScore,
      progress
    });

    return {
      totalExams,
      bestScore,
      averageScore,
      lastScore,
      progress
    };
  };

  // Function to get score from history item
  const getHistoryScore = (item) => {
    const score = item.total_score || item.total || item.score || item.toefl_score || 0;
    return normalizeTOEFLScore(score);
  };

  // LOAD USER + HISTORY
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData) {
          navigate("/", { replace: true });
          return;
        }

        setUser(userData);

        const res = await axios.get("/exam/history");
        
        console.log('📨 Full API Response:', res);
        console.log('📊 API Data:', res.data);

        // Handle various response formats
        let historyData = [];
        if (res.data?.history) {
          historyData = res.data.history;
        } else if (res.data?.data) {
          historyData = res.data.data;
        } else if (Array.isArray(res.data)) {
          historyData = res.data;
        } else {
          historyData = [];
        }

        // Ensure historyData is an array
        historyData = Array.isArray(historyData) ? historyData : [];
        
        // Debug: Log each history item
        console.log('🔍 History items detail:');
        historyData.forEach((item, index) => {
          console.log(`Item ${index}:`, {
            id: item.id,
            total_score: item.total_score,
            total: item.total,
            score: item.score,
            toefl_score: item.toefl_score,
            completed_at: item.completed_at,
            normalized: getHistoryScore(item)
          });
        });

        // Sort by date (newest first)
        historyData.sort((a, b) => {
          const dateA = new Date(a.completed_at || a.date || a.created_at || a.finished_at);
          const dateB = new Date(b.completed_at || b.date || b.created_at || b.finished_at);
          return dateB - dateA;
        });

        setHistory(historyData);

        // Calculate statistics
        const calculatedStats = calculateStats(historyData);
        setStats(calculatedStats);

        // Update user CEFR level
        if (historyData.length > 0) {
          const latestScore = getHistoryScore(historyData[0]);
          const latestCEFR = historyData[0]?.cefr_level || historyData[0]?.cefr;
          
          if (latestCEFR) {
            const updatedUser = {
              ...userData,
              cefr_level: latestCEFR,
              latest_score: latestScore
            };
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));
          }
        }

      } catch (err) {
        console.error('❌ Error fetching dashboard data:', err);
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate("/", { replace: true });
        }
        setHistory([]);
        setStats({
          totalExams: 0,
          bestScore: 0,
          averageScore: 0,
          lastScore: 0,
          progress: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // AUTH / NAV
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearExamData();
    navigate('/', { replace: true });
  };

  const handleStartExam = () => {
    if (window.confirm('Start a new TOEFL exam? Make sure you are ready.')) {
      clearExamData();
      navigate('/exam/listening', { replace: true });
    }
  };

  const handleViewHistory = (examId) => {
    if (examId) {
      navigate(`/exam/results/${examId}`);
    } else {
      navigate('/exam/history');
    }
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Function to get progress indicator
  const getProgressInfo = (progress) => {
    if (progress > 10) return { color: '#26c281', label: 'Rapid Improvement', icon: '🚀' };
    if (progress > 5) return { color: '#26c281', label: 'Steady Growth', icon: '📈' };
    if (progress > 0) return { color: '#26c281', label: 'Slight Improvement', icon: '↗️' };
    if (progress === 0) return { color: '#f7b731', label: 'Stable', icon: '➡️' };
    if (progress > -5) return { color: '#f7b731', label: 'Slight Decline', icon: '↘️' };
    return { color: '#e74c3c', label: 'Needs Improvement', icon: '📉' };
  };

  const progressInfo = getProgressInfo(stats.progress);

  if (loading) {
    return (
      <Box 
        minHeight="100vh" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Box textAlign="center">
          <CircularProgress 
            size={60} 
            sx={{ 
              color: 'white',
              mb: 2
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white',
              fontWeight: 600 
            }}
          >
            Loading Dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  const cefrLevel = user?.cefr_level || 'N/A';
  const userInitial = user.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pb: 6
    }}>
      {/* HEADER */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          color: '#2d3748'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Assessment sx={{ 
              mr: 2, 
              fontSize: 32,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }} />
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
              PRO-EST
            </Typography>
          </Box>

          <Chip 
            label={user.name} 
            avatar={
              <Avatar sx={{ width: 24, height: 24, bgcolor: '#667eea' }}>
                {userInitial}
              </Avatar>
            }
            sx={{ 
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              color: '#2d3748',
              fontWeight: 600,
              mr: 2,
              border: '1px solid rgba(102, 126, 234, 0.2)'
            }}
          />

          <IconButton 
            onClick={handleMenuOpen}
            sx={{ 
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
              color: '#2d3748'
            }}
          >
            <AccountCircle />
          </IconButton>

          <Menu 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleMenuClose}
            PaperProps={{
              sx: { 
                mt: 1.5,
                borderRadius: 3,
                boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                minWidth: 180
              }
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <Person sx={{ mr: 1.5, color: '#667eea' }} /> 
              <Typography variant="body2" fontWeight={600}>Profile</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1.5, color: '#f44336' }} /> 
              <Typography variant="body2" fontWeight={600}>Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* STATISTICS CARDS - IMPROVED */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Assessment sx={{ fontSize: 32 }} />}
              value={stats.totalExams}
              label="Total Exams"
              color="#667eea"
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              subtitle={stats.totalExams > 0 ? `${stats.totalExams} tests completed` : 'No exams yet'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<EmojiEvents sx={{ fontSize: 32 }} />}
              value={stats.bestScore || 0}
              label="Best Score"
              color="#26c281"
              gradient="linear-gradient(135deg, #26c281 0%, #32a852 100%)"
              subtitle={stats.bestScore > 0 ? 'Your highest achievement' : 'No scores yet'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUp sx={{ fontSize: 32 }} />}
              value={stats.averageScore || 0}
              label="Average Score"
              color="#f7b731"
              gradient="linear-gradient(135deg, #f7b731 0%, #f39c12 100%)"
              subtitle={
                stats.averageScore > 0 ? 
                `From ${stats.totalExams} exams` : 
                'No data available'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Grade sx={{ fontSize: 32 }} />}
              value={cefrLevel}
              label="CEFR Level"
              color="#e74c3c"
              gradient="linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)"
              subtitle={stats.lastScore > 0 ? `Latest score: ${stats.lastScore}` : 'No scores yet'}
            />
          </Grid>
        </Grid>

        {/* PROGRESS INDICATOR */}
        {stats.totalExams > 1 && (
          <Card sx={{
            mb: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            border: `2px solid ${progressInfo.color}20`,
            boxShadow: `0 8px 32px ${progressInfo.color}20`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: `${progressInfo.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontSize: '1.5rem'
                  }}>
                    {progressInfo.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color={progressInfo.color}>
                      {progressInfo.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.progress >= 0 ? '+' : ''}{stats.progress}% from previous average
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Latest Score
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color={progressInfo.color}>
                    {stats.lastScore}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* PROFILE CARD */}
        <Card sx={{
          mb: 4,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f7b731 100%)',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16
          }
        }}>
          <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
            <Grid container spacing={4} alignItems="center">
              {/* Avatar Section */}
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Avatar
                  src={user.photo || undefined}
                  alt={user.name}
                  sx={{
                    width: 140,
                    height: 140,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: 48,
                    fontWeight: 800,
                    border: '6px solid white',
                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 16px 48px rgba(102, 126, 234, 0.5)'
                    }
                  }}
                >
                  {!user.photo && userInitial}
                </Avatar>
              </Grid>

              {/* Info Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight={900} sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  {user.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                  {user.email}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                  <Chip
                    label={`CEFR Level: ${cefrLevel}`}
                    sx={{
                      bgcolor: getCEFRColor(cefrLevel),
                      color: 'white',
                      fontWeight: 700,
                      px: 2,
                      py: 1.5,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  />
                  <Chip
                    icon={<School />}
                    label={`${stats.totalExams} Exams Completed`}
                    variant="outlined"
                    sx={{
                      borderColor: '#667eea',
                      color: '#667eea',
                      fontWeight: 600
                    }}
                  />
                  {stats.averageScore > 0 && (
                    <Chip
                      icon={<TrendingUp />}
                      label={`Average: ${stats.averageScore}`}
                      variant="outlined"
                      sx={{
                        borderColor: '#f7b731',
                        color: '#f7b731',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  🎯 {stats.totalExams > 0 
                    ? `You have completed ${stats.totalExams} TOEFL exams. ${stats.progress !== 0 ? `Your score performance has ${stats.progress > 0 ? 'improved' : 'declined'} by ${Math.abs(stats.progress)}% compared to your previous average.` : 'Keep up the consistency!'}`
                    : 'Welcome! Start your TOEFL journey by taking your first exam to track your skill development.'}
                </Typography>
              </Grid>

              {/* Action Section */}
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayCircle />}
                  onClick={handleStartExam}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    px: 4,
                    py: 2,
                    borderRadius: 3,
                    fontWeight: 800,
                    fontSize: '1rem',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    minWidth: 200,
                    '&:hover': { 
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)'
                    }
                  }}
                >
                  {stats.totalExams > 0 ? 'Start New Exam' : 'Start First Exam'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* HISTORY SECTION */}
        <Card sx={{ 
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <History sx={{ mr: 2, fontSize: 32, color: '#667eea' }} />
              <Typography variant="h5" fontWeight={900} sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Recent Exam History
              </Typography>
            </Box>

            {history.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Assessment sx={{ fontSize: 80, color: '#e0e0e0', mb: 3 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                  No Exam History Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                  Begin your TOEFL journey by taking your first exam and track your skill development.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={handleStartExam}
                  startIcon={<PlayCircle />}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 3
                  }}
                >
                  Start First Exam
                </Button>
              </Box>
            ) : (
              <>
                <List sx={{ '& .MuiListItem-root': { borderRadius: 3, mb: 1 } }}>
                  {history.slice(0, 5).map((item, index) => {
                    const score = getHistoryScore(item);
                    const cefr = item.cefr_level || item.cefr || 'N/A';
                    
                    return (
                      <React.Fragment key={item.id || index}>
                        <ListItem 
                          sx={{ 
                            py: 3,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': { 
                              bgcolor: 'rgba(102, 126, 234, 0.05)',
                              transform: 'translateX(8px)',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                            }
                          }}
                          onClick={() => handleViewHistory(item.id)}
                        >
                          <Box sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 3,
                            color: 'white',
                            fontWeight: 900,
                            fontSize: '1.1rem',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                          }}>
                            {index + 1}
                          </Box>
                          
                          <ListItemText
                            primary={
                              <Typography fontWeight={800} fontSize="1.1rem" color="#2d3748">
                                {item.batch || `TOEFL Exam #${history.length - index}`}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <Schedule sx={{ fontSize: 16, mr: 1 }} />
                                  {dayjs(item.completed_at || item.date || item.created_at || item.finished_at).format('DD MMMM YYYY • HH:mm')}
                                </Typography>
                                {item.duration && (
                                  <Typography variant="caption" color="text.secondary">
                                    Duration: {item.duration}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          
                          <Box textAlign="right" sx={{ minWidth: 120 }}>
                            <Typography variant="h5" fontWeight={900} sx={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              mb: 1
                            }}>
                              {score}
                            </Typography>
                            <Chip 
                              label={cefr} 
                              size="small" 
                              sx={{ 
                                bgcolor: getCEFRColor(cefr), 
                                color: 'white',
                                fontWeight: 700,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                              }} 
                            />
                          </Box>
                        </ListItem>
                        {index < Math.min(history.length - 1, 4) && (
                          <Divider sx={{ my: 1, opacity: 0.5 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>

                {history.length > 5 && (
                  <Box textAlign="center" sx={{ mt: 3 }}>
                    <Button 
                      variant="outlined"
                      onClick={() => navigate('/exam/history')}
                      sx={{
                        borderColor: '#667eea',
                        color: '#667eea',
                        fontWeight: 600,
                        borderRadius: 3,
                        px: 4
                      }}
                    >
                      View All History ({history.length})
                    </Button>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

// Enhanced StatCard Component with subtitle
const StatCard = ({ icon, value, label, color, gradient, subtitle }) => (
  <Card sx={{
    borderRadius: 3,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'visible',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 20px 48px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: gradient,
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12
    }
  }}>
    <CardContent sx={{ p: 3, textAlign: 'center' }}>
      <Box sx={{
        width: 64,
        height: 64,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
        background: alpha(color, 0.1),
        color: color,
        transition: 'all 0.3s ease',
      }}>
        {icon}
      </Box>
      <Typography variant="h3" fontWeight="bold" sx={{ 
        background: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 1,
        fontSize: { xs: '2rem', md: '2.5rem' }
      }}>
        {value}
      </Typography>
      <Typography variant="body1" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default Dashboard;
